/**
 * Device Key — keeps the master key usable across browser restarts.
 *
 * The master key normally lives in `sessionStorage`, scrambled against a mask
 * stored beside it. That is obfuscation, not encryption, and `sessionStorage`
 * is wiped when the browser closes — so every reboot or crash forced a full
 * password re-entry even though the server session cookie is good for 30 days.
 *
 * This module closes that gap without putting readable key material on disk:
 *
 *   1. Generate an AES-GCM key with `extractable: false` and keep it in
 *      IndexedDB. The browser stores it in its key store; JavaScript can call
 *      encrypt/decrypt with it but there is no API that returns its bytes.
 *      An XSS payload can *use* it while the page is open, but cannot exfiltrate
 *      it, and it cannot be read out of a copied browser profile.
 *   2. Encrypt the master key under that device key and store the ciphertext.
 *
 * The result: an attacker with the files on disk has ciphertext and a key they
 * cannot export. This is the same non-extractable-CryptoKey trick already used
 * for DEKs in engine/sync/share-revocation.js.
 *
 * What this deliberately does NOT defend against: someone who can run script in
 * your origin while you are logged in (they can ask the device key to decrypt),
 * or someone with your unlocked device and browser profile. Persistent login is
 * a device-trust decision — that is why "Forget this device" exists, and why the
 * record carries an expiry that matches the server session rather than living
 * forever.
 */

const DB_NAME = "orbital-device-key";
const DB_VERSION = 1;
const STORE = "keys";
const DEVICE_KEY_ID = "device-key";
const WRAPPED_ID = "wrapped-master-key";

// Matches the server session lifetime (db/sessions.rs: Duration::days(30)) and
// slides the same way — refreshed on each successful restore. Keeping the two
// in step means there is one lifetime to reason about: if the cookie is still
// good the key is too, and a device untouched for 30 days re-locks on both
// sides at once.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Opt-out lives in localStorage, not IndexedDB: it must be readable
// synchronously at the point where login decides whether to persist, and it is
// a preference rather than a secret.
const DISABLED_KEY = "orbital_device_login_disabled";

/**
 * Persisting the master key needs `crypto.subtle`, which browsers only expose
 * in a secure context (HTTPS, or localhost for development). On plain HTTP this
 * whole module no-ops and the app keeps its previous session-only behaviour.
 */
function isSupported() {
  return (
    typeof indexedDB !== "undefined"
    && typeof crypto !== "undefined"
    && !!crypto.subtle
    && (typeof isSecureContext === "undefined" || isSecureContext)
  );
}

/** True unless the user has explicitly turned persistent login off. */
export function isPersistenceEnabled() {
  try {
    return localStorage.getItem(DISABLED_KEY) !== "1";
  } catch {
    // Storage blocked entirely (some private modes) — treat as disabled rather
    // than throwing on every login.
    return false;
  }
}

/**
 * Turn persistent login on or off for this browser. Turning it off also drops
 * anything already stored, so the toggle takes effect immediately rather than
 * at next logout.
 */
export async function setPersistenceEnabled(enabled) {
  try {
    if (enabled) localStorage.removeItem(DISABLED_KEY);
    else localStorage.setItem(DISABLED_KEY, "1");
  } catch {
    // Non-fatal: the clear below still runs.
  }
  if (!enabled) await clearPersistedMasterKey();
}

function openDb() {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IndexedDB blocked"));
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function idbDelete(db, keys) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    for (const k of keys) store.delete(k);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * Fetch this browser's device key, generating it on first use.
 *
 * `extractable: false` is the entire point of the design — do not relax it. A
 * structured-clone round-trip through IndexedDB preserves that flag, so what
 * comes back out is still non-exportable.
 */
async function getOrCreateDeviceKey(db) {
  const existing = await idbGet(db, DEVICE_KEY_ID);
  if (existing) return existing;

  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false, // non-extractable
    ["encrypt", "decrypt"],
  );
  await idbPut(db, DEVICE_KEY_ID, key);
  return key;
}

/**
 * Encrypt `masterKey` under the device key and store it.
 *
 * `userId` is recorded and checked on the way back out so that signing in as a
 * different account on the same browser can never restore the previous
 * account's key.
 *
 * Failures are swallowed: persistence is a convenience, and a browser that
 * refuses IndexedDB (private mode, disabled storage, quota) should still be
 * able to log in normally.
 *
 * @param {string} userId
 * @param {Uint8Array} masterKey
 * @returns {Promise<boolean>} whether the key was stored
 */
export async function persistMasterKey(userId, masterKey) {
  if (!isSupported() || !isPersistenceEnabled()) return false;
  if (!userId || !(masterKey instanceof Uint8Array) || !masterKey.length) return false;

  let db;
  try {
    db = await openDb();
    const deviceKey = await getOrCreateDeviceKey(db);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      deviceKey,
      masterKey,
    );
    await idbPut(db, WRAPPED_ID, {
      userId,
      iv,
      ciphertext,
      expiresAt: Date.now() + MAX_AGE_MS,
    });
    return true;
  } catch (err) {
    console.warn("[DeviceKey] Could not persist master key:", err?.message || err);
    return false;
  } finally {
    db?.close?.();
  }
}

/**
 * Recover the master key stored for `expectedUserId`.
 *
 * Returns null — never throws — when there is nothing stored, the record has
 * expired, it belongs to a different account, or decryption fails. Every one of
 * those means "ask for the password", which is the safe outcome.
 *
 * A successful restore slides the expiry forward, mirroring the server's
 * session refresh, so an actively used device stays unlocked.
 *
 * @param {string} expectedUserId
 * @returns {Promise<Uint8Array|null>}
 */
export async function restoreMasterKey(expectedUserId) {
  if (!isSupported() || !isPersistenceEnabled()) return null;

  let db;
  try {
    db = await openDb();
    const record = await idbGet(db, WRAPPED_ID);
    if (!record) return null;

    if (!record.expiresAt || Date.now() > record.expiresAt) {
      await idbDelete(db, [WRAPPED_ID]);
      return null;
    }

    // Account switch on a shared browser: the stored key is not this user's.
    // Drop it rather than leaving another account's key material lying around.
    if (expectedUserId && record.userId && record.userId !== expectedUserId) {
      await idbDelete(db, [WRAPPED_ID]);
      return null;
    }

    const deviceKey = await idbGet(db, DEVICE_KEY_ID);
    if (!deviceKey) return null;

    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: record.iv },
      deviceKey,
      record.ciphertext,
    );

    // Slide the expiry the way the server slides the session cookie.
    await idbPut(db, WRAPPED_ID, { ...record, expiresAt: Date.now() + MAX_AGE_MS });

    return new Uint8Array(plain);
  } catch (err) {
    // AES-GCM authentication failure lands here: the ciphertext was tampered
    // with, or the device key was regenerated underneath it. Either way the
    // record is unusable — bin it so we don't retry forever.
    console.warn("[DeviceKey] Could not restore master key:", err?.message || err);
    try {
      if (db) await idbDelete(db, [WRAPPED_ID]);
    } catch { /* best effort */ }
    return null;
  } finally {
    db?.close?.();
  }
}

/**
 * Remove the stored master key. Called on logout and whenever a restored key
 * turns out not to work.
 *
 * Deliberately keeps the device key itself: it is not secret on its own (it
 * decrypts nothing once the wrapped record is gone), and reusing it avoids
 * churning the browser key store on every sign-out/sign-in cycle.
 * `forgetDevice()` is the one that removes everything.
 */
export async function clearPersistedMasterKey() {
  if (typeof indexedDB === "undefined") return;
  let db;
  try {
    db = await openDb();
    await idbDelete(db, [WRAPPED_ID]);
  } catch (err) {
    console.warn("[DeviceKey] Could not clear persisted key:", err?.message || err);
  } finally {
    db?.close?.();
  }
}

/**
 * Drop the device key as well as the wrapped master key, so nothing about this
 * browser's trust survives. Backs "Forget this device".
 */
export async function forgetDevice() {
  if (typeof indexedDB === "undefined") return;
  let db;
  try {
    db = await openDb();
    await idbDelete(db, [DEVICE_KEY_ID, WRAPPED_ID]);
  } catch (err) {
    console.warn("[DeviceKey] Could not forget device:", err?.message || err);
  } finally {
    db?.close?.();
  }
}

/**
 * Whether a usable (unexpired) key is stored for this browser. Used by settings
 * UI to describe the current state without decrypting anything.
 *
 * @returns {Promise<boolean>}
 */
export async function hasPersistedKey() {
  if (!isSupported()) return false;
  let db;
  try {
    db = await openDb();
    const record = await idbGet(db, WRAPPED_ID);
    return !!record && !!record.expiresAt && Date.now() <= record.expiresAt;
  } catch {
    return false;
  } finally {
    db?.close?.();
  }
}
